import React, { useState, useEffect } from "react";
import { post, fmt, shortSvc } from "../api";
import { useDashboard } from "../context/DashboardContext";
import { useAuth } from "../context/AuthContext";

const PILLAR_BG: Record<string, string> = {
  security: "bg-red-500", reliability: "bg-blue-500", performance: "bg-purple-500",
  cost_optimization: "bg-emerald-500", operational_excellence: "bg-amber-500", sustainability: "bg-cyan-500",
};
const PILLAR_BORDER: Record<string, string> = {
  security: "border-l-red-500", reliability: "border-l-blue-500", performance: "border-l-purple-500",
  cost_optimization: "border-l-emerald-500", operational_excellence: "border-l-amber-500", sustainability: "border-l-cyan-500",
};
const PRIO_STYLE: Record<string, string> = {
  high: "text-red-400 bg-red-500/10", medium: "text-amber-400 bg-amber-500/10", low: "text-blue-400 bg-blue-500/10",
};
const MATURITY: Record<string, { label: string; color: string; desc: string }> = {
  foundational: { label: "Foundational", color: "text-red-400", desc: "Basic cloud usage with significant gaps in best practices." },
  intermediate: { label: "Intermediate", color: "text-amber-400", desc: "Some best practices adopted. Key areas need attention." },
  advanced: { label: "Advanced", color: "text-blue-400", desc: "Strong service adoption with minor gaps." },
  optimized: { label: "Optimized", color: "text-emerald-400", desc: "Comprehensive adoption aligned with Well-Architected." },
};

export default function Advisor() {
  const { customer, dateParams } = useDashboard();
  const { user } = useAuth();
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [adminView, setAdminView] = useState(false);
  const [viewMode, setViewMode] = useState<"pillar" | "account">("pillar");

  const isAdmin = user?.role === "admin";
  const insightMonth = dateParams.month || (dateParams.start_date ? dateParams.start_date.slice(0, 7) : "");

  useEffect(() => {
    if (!customer || !insightMonth) return;
    setLoading(true);
    post("/api/advisor/analysis", { customer, month: insightMonth })
      .then(setData).catch(console.error).finally(() => setLoading(false));
  }, [customer, insightMonth]);

  if (loading) return <div className="text-center py-20 text-gray-400">Analyzing cloud maturity...</div>;
  if (!data) return <div className="text-center py-20 text-gray-500">Select a customer to view assessment</div>;

  const mat = MATURITY[data.maturity_level] || MATURITY.foundational;
  const recsByPillar: Record<string, any[]> = {};
  for (const r of data.recommendations) {
    if (!recsByPillar[r.pillar]) recsByPillar[r.pillar] = [];
    recsByPillar[r.pillar].push(r);
  }

  return (
    <div className="animate-fadeIn space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-white">Cloud Maturity Advisor</h1>
        <div className="flex items-center gap-2">
          {/* View toggle */}
          <div className="flex rounded-lg border border-[#2a2d3e] overflow-hidden">
            <button onClick={() => setViewMode("pillar")}
              className={`px-3 py-1.5 text-xs ${viewMode === "pillar" ? "bg-blue-500/20 text-blue-400" : "text-gray-500 hover:text-gray-300"}`}>
              By Pillar
            </button>
            <button onClick={() => setViewMode("account")}
              className={`px-3 py-1.5 text-xs ${viewMode === "account" ? "bg-blue-500/20 text-blue-400" : "text-gray-500 hover:text-gray-300"}`}>
              By Account
            </button>
          </div>
          {isAdmin && (
            <button onClick={() => setAdminView(!adminView)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${adminView ? "bg-purple-500/20 text-purple-400 border border-purple-500/30" : "bg-[#1e2130] text-gray-500 border border-[#2a2d3e]"}`}>
              {adminView ? "Internal" : "Customer"}
            </button>
          )}
        </div>
      </div>

      {/* Summary bar */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-[#1e2130] border border-[#2a2d3e] rounded-xl p-4">
          <div className="text-xs text-gray-400 mb-1">Maturity Level</div>
          <div className={`text-xl font-bold ${mat.color}`}>{mat.label}</div>
          <div className="text-[11px] text-gray-500 mt-1">{mat.desc}</div>
        </div>
        <div className="bg-[#1e2130] border border-[#2a2d3e] rounded-xl p-4">
          <div className="text-xs text-gray-400 mb-1">Services in Use</div>
          <div className="text-xl font-bold text-white">{data.services_count}</div>
          <div className="text-[11px] text-gray-500 mt-1">{data.workload_types.map((w: string) => w.replace(/_/g, " ")).join(", ")}</div>
        </div>
        <div className="bg-[#1e2130] border border-[#2a2d3e] rounded-xl p-4">
          <div className="text-xs text-gray-400 mb-1">Gaps Found</div>
          <div className="text-xl font-bold text-amber-400">{data.recommendations.length}</div>
          <div className="text-[11px] text-gray-500 mt-1">{data.recommendations.filter((r: any) => r.priority === "high").length} high priority</div>
        </div>
        <div className="bg-[#1e2130] border border-[#2a2d3e] rounded-xl p-4">
          <div className="text-xs text-gray-400 mb-1">Accounts</div>
          <div className="text-xl font-bold text-white">{data.by_account?.length || 0}</div>
          {adminView && <div className="text-[11px] text-purple-400 mt-1">Total: {fmt(data.total_spend)}</div>}
        </div>
      </div>

      {/* Pillar score bars */}
      <div className="bg-[#1e2130] border border-[#2a2d3e] rounded-xl p-5">
        <div className="flex items-center justify-between mb-1">
          <div className="text-base font-semibold text-gray-100">Well-Architected Pillar Scores</div>
        </div>
        <div className="text-xs text-gray-500 mb-4">Based on aggregate service usage across all accounts under this customer</div>
        <div className="space-y-3">
          {Object.entries(data.pillar_scores).map(([key, p]: [string, any]) => {
            const pct = p.max > 0 ? Math.round((p.score / p.max) * 100) : 0;
            const gaps = recsByPillar[key]?.length || 0;
            return (
              <div key={key} className="flex items-center gap-4">
                <div className="w-40 text-sm text-gray-300 shrink-0">{p.label}</div>
                <div className="flex-1 h-3 rounded-full bg-[#0f1117] overflow-hidden">
                  <div className={`h-full rounded-full ${PILLAR_BG[key]} transition-all`} style={{ width: `${pct}%` }} />
                </div>
                <div className="w-16 text-right text-sm font-medium text-gray-300">{p.score}/{p.max}</div>
                {gaps > 0 && <span className="text-[10px] text-amber-400 w-16">{gaps} gaps</span>}
              </div>
            );
          })}
        </div>
      </div>

      {/* 3-Month Service Trends — compact collapsible */}
      {data.service_trends?.length > 0 && (() => {
        const currentMonth = new Date().toISOString().slice(0, 7);
        const hasPartial = data.service_trends.some((t: any) => t.months.some((m: any) => m.month === currentMonth));
        return (
          <details className="bg-[#1e2130] border border-[#2a2d3e] rounded-xl overflow-hidden group">
            <summary className="px-5 py-3 cursor-pointer hover:bg-[#252840] transition-colors flex items-center justify-between list-none">
              <div className="flex items-center gap-3">
                <span className="text-base font-semibold text-gray-100">3-Month Usage Trends</span>
                <span className="px-2 py-0.5 rounded-full text-[10px] bg-[#2a2d3e] text-gray-300">{data.service_trends.length} services</span>
              </div>
              <span className="text-gray-400 text-xs group-open:rotate-180 transition-transform">{"\u25BE"}</span>
            </summary>
            <div className="px-5 pb-4 border-t border-[#2a2d3e]">
              {hasPartial && (
                <div className="text-[11px] text-amber-400/70 mt-2 mb-2">Current month is partial — growth % is based on completed months only</div>
              )}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-6 gap-y-0.5 mt-1">
                {data.service_trends.slice(0, 20).map((t: any) => {
                  const dirColor = t.direction === "growing" ? "text-red-400" : t.direction === "shrinking" ? "text-emerald-400" : t.direction === "new" ? "text-purple-400" : "text-gray-500";
                  const dirLabel = t.growth_pct !== 0 ? `${t.growth_pct > 0 ? "+" : ""}${t.growth_pct}%` : t.direction === "new" ? "NEW" : "—";
                  const maxCost = Math.max(...t.months.map((m: any) => m.cost), 1);
                  return (
                    <div key={t.service} className="flex items-center gap-2 py-1">
                      <div className="w-28 text-xs text-gray-300 truncate shrink-0">{shortSvc(t.service)}</div>
                      <div className="flex items-end gap-px h-3 w-16 shrink-0">
                        {t.months.map((m: any, i: number) => {
                          const isPartial = m.month === currentMonth;
                          return (
                            <div key={i} className={`flex-1 rounded-sm ${isPartial ? "bg-gray-600/50 border border-dashed border-gray-500" : t.direction === "growing" ? "bg-red-500/50" : t.direction === "shrinking" ? "bg-emerald-500/50" : "bg-blue-500/30"}`}
                              style={{ height: `${Math.max(2, (m.cost / maxCost) * 100)}%` }}
                              title={`${m.month}: ${fmt(m.cost)}${isPartial ? " (partial)" : ""}`} />
                          );
                        })}
                      </div>
                      <div className="w-16 text-right text-xs text-gray-400 tabular-nums">{fmt(t.total_3mo)}</div>
                      <div className={`w-12 text-right text-[11px] font-medium ${dirColor}`}>{dirLabel}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          </details>
        );
      })()}

      {/* Trend-Based Recommendations */}
      {data.trend_recommendations?.length > 0 && (
        <div className="bg-[#1e2130] border border-[#2a2d3e] border-l-4 border-l-cyan-500 rounded-xl p-5">
          <div className="text-base font-semibold text-gray-100 mb-1">Next Steps — Growth Opportunities</div>
          <div className="text-xs text-gray-500 mb-4">New workloads and services to consider based on your usage growth patterns</div>
          <div className="space-y-2">
            {data.trend_recommendations.map((tr: any, i: number) => (
              <TrendRecCard key={i} rec={tr} adminView={adminView} />
            ))}
          </div>
        </div>
      )}

      {/* ── PILLAR VIEW — Best Practice Gaps ── */}
      {viewMode === "pillar" && (
        <div className="space-y-4">
          <div className="text-base font-semibold text-gray-100">Best Practice Gaps</div>
          {Object.entries(recsByPillar).map(([pillar, recs]) => {
            const info = data.pillar_info[pillar];
            return (
              <div key={pillar} className={`bg-[#1e2130] border border-[#2a2d3e] border-l-4 ${PILLAR_BORDER[pillar] || ""} rounded-xl p-5`}>
                <div className="text-base font-semibold text-gray-100 mb-1">{info?.label || pillar}</div>
                <div className="text-xs text-gray-500 mb-4">{info?.description}</div>
                <div className="space-y-2">
                  {recs.map((r: any, i: number) => (
                    <RecCard key={i} rec={r} adminView={adminView} />
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ── ACCOUNT VIEW — Heatmap ── */}
      {viewMode === "account" && data.by_account?.length > 0 && (() => {
        const cats = ["compute", "containers", "database", "storage", "networking", "security", "monitoring", "backup", "ml", "devtools"];
        const catLabels: Record<string, string> = { compute: "Compute", containers: "Containers", database: "Database", storage: "Storage", networking: "Network", security: "Security", monitoring: "Monitor", backup: "Backup", ml: "AI/ML", devtools: "DevTools" };
        return (
          <div className="space-y-4">
            <div className="text-base font-semibold text-gray-100">Service Adoption by Account</div>

            {/* Heatmap table */}
            <div className="bg-[#1e2130] border border-[#2a2d3e] rounded-xl overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-[#1a1d27]">
                    <th className="text-left px-3 py-2.5 text-gray-300 font-semibold sticky left-0 bg-[#1a1d27] min-w-[160px]">Account</th>
                    {adminView && <th className="text-right px-2 py-2.5 text-gray-400 font-medium">Spend</th>}
                    <th className="text-center px-1 py-2.5 text-gray-400 font-medium">Svcs</th>
                    {cats.map(c => (
                      <th key={c} className="text-center px-1.5 py-2.5 text-gray-400 font-medium whitespace-nowrap">{catLabels[c]}</th>
                    ))}
                    <th className="text-center px-2 py-2.5 text-gray-400 font-medium">Gaps</th>
                  </tr>
                </thead>
                <tbody>
                  {data.by_account.map((acct: any) => (
                    <tr key={acct.account_id} className="border-t border-[#2a2d3e] hover:bg-[#252840]/50">
                      <td className="px-3 py-2 sticky left-0 bg-[#1e2130]">
                        <div className="text-sm text-gray-200 font-medium truncate max-w-[160px]">{acct.account_name}</div>
                        <div className="text-[10px] text-gray-500">{acct.account_id}</div>
                      </td>
                      {adminView && <td className="text-right px-2 py-2 text-sm font-semibold text-purple-300 tabular-nums">{fmt(acct.spend)}</td>}
                      <td className="text-center px-1 py-2 text-gray-400">{acct.service_count}</td>
                      {cats.map(c => {
                        const cat = acct.categories?.[c];
                        const active = cat?.active;
                        const spend = cat?.spend || 0;
                        const svcList = cat?.services?.map((s: string) => s.replace("Amazon","").replace("AWS","").trim()).join(", ") || "";
                        return (
                          <td key={c} className="text-center px-1.5 py-2" title={active ? `${catLabels[c]}: ${svcList}\nSpend: ${fmt(spend)}` : `No ${catLabels[c]} services`}>
                            {active ? (
                              <div className={`w-6 h-6 rounded mx-auto flex items-center justify-center ${spend > 500 ? "bg-blue-500/30" : spend > 50 ? "bg-blue-500/20" : "bg-blue-500/10"}`}>
                                <svg className="w-3.5 h-3.5 text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" /></svg>
                              </div>
                            ) : (
                              <div className="w-6 h-6 rounded mx-auto bg-[#0f1117] border border-[#2a2d3e] flex items-center justify-center">
                                <span className="text-[10px] text-gray-600">—</span>
                              </div>
                            )}
                          </td>
                        );
                      })}
                      <td className="text-center px-2 py-2">
                        {acct.missing_critical.length > 0 ? (
                          <span className="text-amber-400 font-medium">{acct.missing_critical.length}</span>
                        ) : (
                          <span className="text-emerald-400 text-[10px]">OK</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Legend */}
            <div className="flex flex-wrap items-center gap-4 text-[10px] text-gray-500 px-1">
              <span className="flex items-center gap-1.5"><span className="w-4 h-4 rounded bg-blue-500/30 inline-flex items-center justify-center"><svg className="w-2.5 h-2.5 text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg></span> In use</span>
              <span className="flex items-center gap-1.5"><span className="w-4 h-4 rounded bg-[#0f1117] border border-[#2a2d3e] inline-flex items-center justify-center"><span className="text-[8px] text-gray-600">—</span></span> Not in use</span>
              <span>Brighter = higher spend in that category</span>
              <span>Hover for service names and spend</span>
            </div>

            {/* Accounts with critical gaps — expanded detail */}
            {data.by_account.filter((a: any) => a.missing_critical.length > 0).length > 0 && (
              <div className="bg-[#1e2130] border border-[#2a2d3e] border-l-4 border-l-amber-500 rounded-xl p-5">
                <div className="text-base font-semibold text-gray-100 mb-3">Accounts with Critical Gaps</div>
                <div className="space-y-2">
                  {data.by_account.filter((a: any) => a.missing_critical.length > 0).map((acct: any) => (
                    <div key={acct.account_id} className="flex items-center justify-between py-2 px-3 rounded-lg bg-[#0f1117]/50">
                      <div>
                        <span className="text-sm text-gray-200">{acct.account_name}</span>
                        {adminView && <span className="text-xs text-purple-400 ml-2">{fmt(acct.spend)}</span>}
                      </div>
                      <div className="flex gap-1.5">
                        {acct.missing_critical.map((s: string) => (
                          <span key={s} className="px-2 py-0.5 rounded bg-amber-500/10 border border-amber-500/20 text-[10px] text-amber-300">{s}</span>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        );
      })()}

      {data.recommendations.length === 0 && (
        <div className="bg-[#1e2130] border border-emerald-500/30 rounded-xl p-8 text-center">
          <div className="text-lg font-semibold text-emerald-400">Excellent Cloud Maturity</div>
          <div className="text-sm text-gray-400 mt-2">No significant gaps detected.</div>
        </div>
      )}
    </div>
  );
}

function RecCard({ rec, adminView }: { rec: any; adminView: boolean }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-lg border border-[#2a2d3e] overflow-hidden">
      <button onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-[#252840]/50 transition-colors">
        <div className="flex items-center gap-2.5 flex-1 min-w-0">
          <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium shrink-0 ${PRIO_STYLE[rec.priority]}`}>{rec.priority}</span>
          <span className="text-sm font-medium">{rec.title}</span>
        </div>
        <div className="flex items-center gap-2 shrink-0 ml-3">
          {adminView && rec.revenue_potential && (
            <span className={`px-1.5 py-0.5 rounded text-[10px] ${rec.revenue_potential === "high" ? "text-emerald-400 bg-emerald-500/10" : rec.revenue_potential === "medium" ? "text-blue-400 bg-blue-500/10" : "text-gray-400 bg-[#2a2d3e]"}`}>
              {rec.revenue_potential}
            </span>
          )}
          <span className={`text-gray-500 text-xs transition-transform ${open ? "rotate-180" : ""}`}>{"\u25BE"}</span>
        </div>
      </button>
      {open && (
        <div className="px-4 pb-4 pt-2 border-t border-[#2a2d3e]/50 bg-[#0f1117]/40">
          <p className="text-sm text-gray-300 leading-relaxed mb-3">{rec.description}</p>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-2">
            {/* Benefit */}
            <div className="p-2.5 rounded-lg bg-blue-500/5 border border-blue-500/10">
              <div className="text-[10px] text-blue-400 uppercase tracking-wider mb-1">Benefit</div>
              <div className="text-xs text-blue-300/80 leading-relaxed">{rec.benefit}</div>
            </div>

            {/* Considerations — admin only */}
            {adminView && rec.considerations && (
              <div className="p-2.5 rounded-lg bg-red-500/5 border border-red-500/10">
                <div className="text-[10px] text-red-400 uppercase tracking-wider mb-1">Considerations</div>
                <div className="text-xs text-red-300/70 leading-relaxed">{rec.considerations}</div>
              </div>
            )}
          </div>

          {/* Cost + Revenue — admin only */}
          {adminView && rec.estimated_monthly_cost && (
            <div className="flex gap-4 mt-2 text-xs">
              <span className="text-purple-400">Cost: <span className="text-purple-300">{rec.estimated_monthly_cost}</span></span>
              {rec.revenue_potential && <span className="text-purple-400">Revenue: <span className="text-purple-300 capitalize">{rec.revenue_potential}</span></span>}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function TrendRecCard({ rec, adminView }: { rec: any; adminView: boolean }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-lg border border-[#2a2d3e] overflow-hidden">
      <button onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-[#252840]/50 transition-colors">
        <div className="flex items-center gap-2.5 flex-1 min-w-0">
          <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium shrink-0 ${PRIO_STYLE[rec.priority]}`}>{rec.priority}</span>
          <span className="text-sm font-medium">{rec.title}</span>
        </div>
        <span className={`text-gray-500 text-xs transition-transform ${open ? "rotate-180" : ""}`}>{"\u25BE"}</span>
      </button>
      {open && (
        <div className="px-4 pb-4 pt-2 border-t border-[#2a2d3e]/50 bg-[#0f1117]/40 space-y-2">
          <div className="text-[11px] text-cyan-400 bg-cyan-500/5 border border-cyan-500/10 rounded px-2.5 py-1.5">
            Triggered by: {rec.trigger}
          </div>
          <p className="text-sm text-gray-300 leading-relaxed">{rec.description}</p>
          <div className="flex flex-wrap gap-1.5">
            {rec.services_to_consider.map((s: string) => (
              <span key={s} className="px-2 py-0.5 rounded bg-cyan-500/10 border border-cyan-500/20 text-[11px] text-cyan-300">{s}</span>
            ))}
          </div>
          {adminView && rec.estimated_impact && (
            <div className="text-xs text-purple-400 mt-1">Impact: <span className="text-purple-300">{rec.estimated_impact}</span></div>
          )}
        </div>
      )}
    </div>
  );
}
